<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import Table from '../Table.svelte';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import { options } from '../../configs/BarOptions';
  import DateRange from '../common/DateRange.svelte';
  import { resetChart, colors } from '../../utils/common';
  import LigamentSelect from '../common/LigamentSelect.svelte';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import BaseCurrencySelect from '../common/BaseCurrencySelect.svelte';
  import { getSwapAnalytics } from '../../../#services/getSwapAnalytics';

  let chart, table;

  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let ligament = 'WLIS/NEAR';
  let token = '0';

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2023-04-10'
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refresh() {
    resetChart(chart, labels, datasets);

    const requestData = {
      ligament,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    };

    getSwapAnalytics.send({ data: requestData });
  }

  getSwapAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      value.value.data.forEach(e => {
        labels.push(e.date);
      });

      const tokenKey = token === '0' ? 'first' : 'second';

      datasets.push({
        label: `Total Balance, ${ligament.split('/')[+token]}`,
        backgroundColor: colors[0],
        borderColor: colors[0],
        fill: false,
        stack: 0,
        data: value.value.data.map((d) => d.balances[`${tokenKey}TokenTotalBalance`]),
      });

      datasets.push({
        label: `Total Volume, ${ligament.split('/')[+token]}`,
        backgroundColor: colors[1],
        borderColor: colors[1],
        fill: false,
        stack: 0,
        data: value.value.data.map((d) => d.autoswaps[`${tokenKey}TokenTotalVolume`]),
      });

      try {
        table = {
          [`➚ Volume, ${ligament.split('/')[0]}`]: value.value.data.map((v) => ({ amount: v.autoswaps.firstTokenVolume, point: v.date, rawDate: v.rawDate })),
          [`➚ Volume, ${ligament.split('/')[1]}`]: value.value.data.map((v) => ({ amount: v.autoswaps.secondTokenVolume, point: v.date })),
          [`➚ Total Volume, ${ligament.split('/')[+token]}`]: value.value.data.map((v) => ({ amount: v.autoswaps[`${tokenKey}TokenTotalVolume`], point: v.date })),
          '➚ Autoswaps Count, N': value.value.data.map((v) => ({ amount: +v.autoswaps.firstTokenCount + +v.autoswaps.secondTokenCount, point: v.date })),
          [`💯 Balance, ${ligament.split('/')[0]}`]: value.value.data.map((v) => ({ amount: v.balances.firstTokenBalance, point: v.date })),
          [`💯 Difference, ${ligament.split('/')[0]}`]: value.value.data.map((v) => ({ amount: v.balances.firstTokenDifference, point: v.date })),
          [`💯 Balance, ${ligament.split('/')[1]}`]: value.value.data.map((v) => ({ amount: v.balances.secondTokenBalance, point: v.date })),
          [`💯 Difference, ${ligament.split('/')[1]}`]: value.value.data.map((v) => ({ amount: v.balances.secondTokenDifference, point: v.date })),
          [`💯 Total Balance, ${ligament.split('/')[+token]}`]: value.value.data.map((v) => ({ amount: v.balances[`${tokenKey}TokenTotalBalance`], point: v.date })),
          [`💯 Profit, ${ligament.split('/')[0]}`]: value.value.data.map((v) => ({ amount: v.balances.firstTokenProfit <= 0 ? '—' : v.balances.firstTokenProfit, point: v.date })),
          [`💯 Profit, ${ligament.split('/')[1]}`]: value.value.data.map((v) => ({ amount: v.balances.secondTokenProfit <= 0 ? '—' : v.balances.secondTokenProfit, point: v.date })),
        }
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <LigamentSelect bind:ligament={ligament} refreshCallback={() => refresh()} />
  </div>
  <div class="pc-line">
    <BaseCurrencySelect bind:ligament={ligament} bind:token={token} refreshCallback={() => refresh()} />
  </div>
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">MM Swaps Analytics.</p>
<div class="graphic">
  {#if $getSwapAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getSwapAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getSwapAnalytics.value}
    <Chart bind:this={chart} type="bar" {data} width={500} height={270} {options} />
    <Table branches={table} isTableHidden={false} totalOnlyFor={[0, 1, 3]} borders={[3, 5, 7, 8]} />
  {/if}
</div>